/**
 * Chatwoot Conversation Types
 *
 * Type definitions for conversation-related API requests and responses
 * @see Conversation.md
 */

/**
 * Conversation Status Enum
 * Filter conversations by status
 */
export enum ConversationStatus {
  ALL = "all",
  OPEN = "open",
  RESOLVED = "resolved",
  PENDING = "pending",
  SNOOZED = "snoozed",
}

/**
 * Conversation Priority Enum
 * Priority levels for conversations
 */
export enum ConversationPriority {
  URGENT = "urgent",
  HIGH = "high",
  MEDIUM = "medium",
  LOW = "low",
  NONE = "none",
}

/**
 * Conversation Assignee Type Enum
 * Filter by assignee
 */
export enum ConversationAssigneeType {
  ME = "me",
  UNASSIGNED = "unassigned",
  ALL = "all",
  ASSIGNED = "assigned",
}

/**
 * Conversation Sort By Options
 * Sort order for conversations
 */
export enum ConversationSortBy {
  LAST_ACTIVITY_AT_DESC = "last_activity_at_desc",
  LAST_ACTIVITY_AT_ASC = "last_activity_at_asc",
  CREATED_AT_DESC = "created_at_desc",
  CREATED_AT_ASC = "created_at_asc",
}

/**
 * Conversation Type Enum
 * Pre-defined filter types that translate to specific backend queries
 */
export enum ConversationType {
  /** Shows conversations that require attention (never replied to OR waiting for contact response) */
  UNATTENDED = "unattended",
  /** Shows conversations where the current user has been mentioned */
  MENTION = "mention",
  /** Shows conversations the user is actively involved in (participating) */
  PARTICIPATING = "participating",
}

/**
 * Conversation List Query Parameters
 * Query parameters for GET /api/accounts/conversations
 */
export interface ConversationListParams {
  /** Filter conversations by legacy status (compat) */
  status?: ConversationStatus | ConversationStatus[];

  /** Filter by workflow status definition publicId (Phase 3) */
  status_id?: number | string;

  /** Filter by assignee */
  assignee_type?: ConversationAssigneeType;

  /** Pre-defined conversation type filter (unattended, mention, participating) */
  conversation_type?: ConversationType;

  /** Sort order */
  sort_by?: ConversationSortBy | string;

  /** Page number for pagination (starts at 1) */
  page?: number;

  /** Search query string to filter conversations */
  q?: string;

  /** Filter by specific inbox ID */
  inbox_id?: number;

  /** Filter by specific team ID */
  team_id?: number;

  /** Filter by conversation labels (multiple labels can be passed) */
  labels?: string | string[];

  /** Limit number of results per page */
  limit?: number;

  /** Ticket lifetime view: active tabs (default) or Archive (expired tickets only) */
  lifecycle?: ConversationLifecycleView;
}

export type ConversationLifecycleView = "active" | "archived";

export type ConversationLifecycleState = "active" | "expired";

export type ExpirationRuleType = "inactivity" | "workflow_status";
export type ExpirationDurationUnit = "hours" | "days";

/** One expiration rule; rules are OR-ed (any match expires the ticket). */
export interface ConversationExpirationRule {
  id: number;
  type: ExpirationRuleType;
  status_id: number | null;
  status_name: string | null;
  status_color: string | null;
  duration_value: number;
  duration_unit: ExpirationDurationUnit;
  enabled: boolean;
}

export interface ConversationExpirationPolicy {
  enabled: boolean;
  rules: ConversationExpirationRule[];
  updated_at: number | null;
}

export interface ConversationExpirationRuleInput {
  /** Omit for new rules */
  id?: number;
  type: ExpirationRuleType;
  status_id?: number | null;
  duration_value: number;
  duration_unit: ExpirationDurationUnit;
  enabled: boolean;
}

export interface ConversationExpirationPolicyInput {
  enabled: boolean;
  rules: ConversationExpirationRuleInput[];
}

/** Why / by whom an archived ticket expired (detail payload only). */
export interface ConversationExpirationDetails {
  reason: "manual" | "automatic";
  actor_name?: string | null;
  rule_description?: string | null;
  rule?: {
    type?: string;
    duration_value?: number;
    duration_unit?: string;
    workflow_status_name?: string | null;
  } | null;
  expired_at?: number | null;
}

/**
 * Workflow status definition embedded on conversation responses (Phase 3).
 */
export interface ConversationWorkflowStatus {
  id: number;
  uuid?: string;
  name: string;
  slug?: string | null;
  type: "system" | "custom" | string;
  system_type?: "open" | "resolved" | "snoozed" | null;
  color: string;
  sort_order?: number;
  is_active?: boolean;
}

/**
 * Chatwoot Conversation
 * Represents a conversation in Chatwoot
 */
export interface ChatwootConversation {
  id?: number;
  messages?: ChatwootMessage[];
  account_id?: number;
  uuid?: string;
  additional_attributes?: Record<string, any>;
  agent_last_seen_at?: number;
  assignee_last_seen_at?: number;
  can_reply?: boolean;
  contact_last_seen_at?: number;
  custom_attributes?: Record<string, any>;
  inbox_id?: number;
  labels?: string[];
  muted?: boolean;
  starred?: boolean;
  snoozed_until?: number;
  /** Compat lifecycle status (may fold Waiting → pending) */
  status?: string;
  /** Workflow status definition — UI display source of truth */
  workflow_status?: ConversationWorkflowStatus | null;
  created_at?: number;
  updated_at?: number;
  timestamp?: string;
  first_reply_created_at?: number;
  unread_count?: number;
  last_non_activity_message?: ChatwootMessage;
  last_activity_at?: number;
  priority?: string;
  waiting_since?: number;
  sla_policy_id?: number;
  applied_sla?: Record<string, any>;
  sla_events?: Array<Record<string, any>>;
  meta?: {
    sender?: {
      id?: number;
      name?: string;
      email?: string;
      avatar_url?: string;
      phone_number?: string;
      identifier?: string;
      availability_status?: string;
      blocked?: boolean;
      additional_attributes?: Record<string, any>;
      custom_attributes?: Record<string, any>;
      last_activity_at?: number;
      created_at?: number;
      thumbnail?: string;
      availabilityStatus?: string;
    };
    channel?: string;
    assignee?: {
      id?: number;
      name?: string;
      email?: string;
      thumbnail?: string;
      availability_status?: string;
      account_id?: number;
      avatar_url?: string;
    } | null;
    /** All assigned agents (multi-agent assignment) */
    assignees?: Array<{
      id?: number | string;
      name?: string;
      email?: string;
      thumbnail?: string;
      availability_status?: string;
      account_id?: number;
      avatar_url?: string;
    }>;
    team?: {
      id?: number;
      name?: string;
      description?: string;
    } | null;
    hmac_verified?: boolean;
    /** Phase 1–3 merge metadata (native FrontDesk) */
    merge?: ConversationMergeMetadata;
  };
  /** Top-level merge metadata (native FrontDesk Phase 3) */
  merge?: ConversationMergeMetadata;
  /** Ticket lifetime: expired tickets live in Archive and are read-only */
  lifecycle_state?: ConversationLifecycleState;
  /** Unix seconds; set while the ticket is archived */
  expired_at?: number | null;
  expiration_reason?: "manual" | "automatic" | null;
  /** Archived ticket this one succeeded (inbound after expiration) */
  predecessor_conversation_id?: string | null;
  expiration?: ConversationExpirationDetails | null;
}

/** Merged conversation member / path metadata */
export interface ConversationMergeMember {
  conversation_id: string;
  role: 'canonical' | 'absorbed';
  inbox_id?: string | null;
  inbox_public_id?: number | null;
  channel_type?: 'email' | 'whatsapp' | null;
  provider?: 'gmail' | 'outlook' | 'whatsapp' | null;
  inbox_name?: string | null;
  inbox_address?: string | null;
  provider_thread_id?: string | null;
  contact_id?: string | null;
  customer_destination?: string | null;
  last_message_at?: string | null;
}

export interface ConversationMergeMetadata {
  is_absorbed?: boolean;
  is_merged?: boolean;
  merged_into_conversation_id?: string | null;
  canonical_conversation_id?: string;
  member_conversation_ids?: string[];
  member_count?: number;
  members?: ConversationMergeMember[];
}

/** Phase 4: outbound communication path for path picker / send */
export interface CommunicationPath extends ConversationMergeMember {
  is_canonical?: boolean;
  last_inbound_at?: string | null;
  usable?: boolean;
  /**
   * Persistent Create Customer reply destination of THIS path (email only), or null. Replies sent
   * through the path go here; `customer_destination` stays the mailbox that originally wrote.
   */
  reply_destination?: ReplyDestination | null;
}

export interface ReplyDestination {
  email: string;
  basis: string;
  original_sender: string | null;
}

export interface CommunicationPathsPayload {
  canonical_conversation_id: string;
  default_path_conversation_id: string | null;
  paths: CommunicationPath[];
}

export interface CommunicationPathsResponse {
  payload?: CommunicationPathsPayload;
  data?: { payload?: CommunicationPathsPayload };
}

/** Automation Auto Send ledger state for one inbound of the ticket (messaging-hub). */
export interface AutoSendStatePayload {
  inbound_message_key: string | null;
  auto_send_id: string | null;
  status: string | null;
  sending_disabled: boolean;
  error_code: string | null;
  suggestion_state: string;
}

export interface AutoSendStateResponse {
  payload?: AutoSendStatePayload;
  data?: { payload?: AutoSendStatePayload };
}

/** Phase 1 merge candidate (same gomakeCustomerId roots) */
export interface MergeCandidate {
  id: string;
  inbox_id: number | string | null;
  subject: string | null;
  status: string;
  channel_type: "email" | "whatsapp" | string;
  /** gmail | outlook | whatsapp — drives brand icons in Merge UI */
  provider?: "gmail" | "outlook" | "whatsapp" | string | null;
  last_activity_at: string | null;
  contact: {
    id: string | number;
    name?: string | null;
    email?: string | null;
    phone_number?: string | null;
    gomake_customer_id?: string | null;
  } | null;
}

export interface MergeCandidatesResponse {
  payload?: MergeCandidate[];
  data?: { payload?: MergeCandidate[] };
}

/** POST /conversations/:id/merge result */
export interface MergeConversationsResult {
  canonical_conversation_id: string;
  absorbed_conversation_ids: string[];
  member_conversation_ids?: string[];
  merge_id?: string;
  already_merged: boolean;
  merge?: ConversationMergeMetadata;
}

export interface MergeConversationsResponse {
  payload?: MergeConversationsResult;
  data?: { payload?: MergeConversationsResult };
}

/** POST /conversations/:id/unmerge result */
export interface UnmergeConversationsResult {
  canonical_conversation_id: string;
  unmerged_conversation_id: string;
  remaining_member_conversation_ids?: string[];
  already_unmerged: boolean;
  unmerge_id?: string;
  merge?: ConversationMergeMetadata;
  unmerged_conversation?: ChatwootConversation | null;
}

export interface UnmergeConversationsResponse {
  payload?: UnmergeConversationsResult;
  data?: { payload?: UnmergeConversationsResult };
}

/** Bounded FrontDesk timeline prior to the AI input message (hub AiConversationContextDto). */
export interface AiAssistConversationHistory {
  canonical_conversation_id: string;
  source_conversation_id: string | null;
  is_merged: boolean;
  truncated: boolean;
  included_message_count: number;
  omitted_message_count: number;
  messages: Array<{
    direction: "incoming" | "outgoing";
    role: "customer" | "agent";
    channel: "gmail" | "outlook" | "whatsapp" | "email";
    content: string;
    created_at: string;
    has_attachments: boolean;
    source_conversation_id: string;
  }>;
  formatted_text: string;
}

/** GET /conversations/:id/ai_assist_input result */
export interface AiAssistInput {
  conversation_id: string;
  /** Latest stored incoming, non-private message; null when the customer has not written */
  latest_inbound: ChatwootMessage | null;
  conversation_history: AiAssistConversationHistory | null;
  /** Hub Message.id of the latest inbound (for Manual Assist automation identity) */
  hub_message_id?: string | null;
  /** Provider (Gmail/Outlook/WhatsApp) message id when known */
  provider_message_id?: string | null;
  /** Member conversation the inbound arrived on (= ticket when not merged) */
  source_conversation_id?: string | null;
  /** Stable inbound key (pm:… / hm:…) matching the Automation run */
  inbound_message_key?: string | null;
  /**
   * Frozen Automation catalog for this inbound (same handoff as automatic AI).
   * Manual Assist evaluates it for answers; auto-send stays blocked for MANUAL.
   */
  frontdesk_automation_run?: Record<string, unknown> | null;
}

export interface AiAssistInputResponse {
  payload?: AiAssistInput;
  data?: { payload?: AiAssistInput };
}

/**
 * Chatwoot Email Content Structure
 * How Chatwoot stores email HTML and plain text versions
 * Note: Chatwoot API returns snake_case properties
 */
export interface ChatwootEmailContent {
  /** Email subject line */
  subject?: string;
  /** HTML version of the email */
  html_content?: {
    full?: string;
  };
  /** Plain text version of the email */
  text_content?: {
    full?: string;
  };
}

/**
 * Chatwoot Message Content Attributes
 * Structured data for different message types
 */
export interface ChatwootContentAttributes {
  /** Email-specific content (for Channel::Email messages) */
  email?: ChatwootEmailContent;
  /** Generic HTML content (for backward compatibility) */
  html?: string;
  /** Other dynamic attributes */
  [key: string]: any;
}

/**
 * Chatwoot Attachment
 * Represents a file attachment on a message
 */
export interface ChatwootAttachment {
  id: number;
  message_id: number;
  file_type: "image" | "audio" | "video" | "file" | "location" | "contact" | "fallback";
  account_id: number;
  extension?: string | null;
  data_url: string;
  thumb_url?: string;
  file_size?: number;
  width?: number;
  height?: number;
}

/**
 * Chatwoot Message (simplified)
 * Used within conversations
 */
export interface ChatwootMessage {
  id?: number;
  content?: string;
  account_id?: number;
  inbox_id?: number;
  conversation_id?: number;
  /** Member conversation that owns this message (merged timelines) */
  source_conversation_id?: string | number;
  message_type?: 0 | 1 | 2; // 0: incoming, 1: outgoing, 2: activity
  created_at?: number;
  updated_at?: number;
  /** Private flag - API may return as _private or private */
  _private?: boolean;
  /** Private flag - API may return as _private or private */
  private?: boolean;
  status?: 'sent' | 'delivered' | 'read' | 'failed';
  source_id?: string;
  content_type?: string;
  content_attributes?: ChatwootContentAttributes;
  sender_type?: string;
  sender_id?: number;
  sender?: {
    id?: number;
    name?: string;
    email?: string;
    avatar_url?: string;
  };
  /** Message attachments (images, files, etc.) */
  attachments?: ChatwootAttachment[];
  /** Phase 3 communication path (also mirrored in content_attributes) */
  communication_path?: {
    source_conversation_id?: string;
    inbox_id?: string | null;
    inbox_public_id?: number | null;
    channel_type?: 'email' | 'whatsapp' | null;
    provider?: 'gmail' | 'outlook' | 'whatsapp' | null;
    inbox_name?: string | null;
    inbox_address?: string | null;
    customer_destination?: string | null;
  };
}

/**
 * Conversation List Response
 * Response structure for list conversations endpoint
 */
export interface ConversationListResponse {
  data?: {
    payload?: ChatwootConversation[];
    meta?: {
      page?: number;
      total_pages?: number;
      total_count?: number;
      count?: number;
      per_page?: number;
      mine_count?: number;
      unassigned_count?: number;
      all_count?: number;
      /** Per workflow-status definition counts (publicId → count) */
      status_counts?: Array<{ status_id?: number; count?: number }>;
    };
  };
  payload?: ChatwootConversation[];
  meta?: {
    page?: number;
    total_pages?: number;
    total_count?: number;
    count?: number;
    per_page?: number;
    mine_count?: number;
    unassigned_count?: number;
    all_count?: number;
    status_counts?: Array<{ status_id?: number; count?: number }>;
  };
}

/**
 * Conversation Detail Response
 * Response structure for get conversation details endpoint
 * 
 * Note: Chatwoot returns the conversation directly (not wrapped),
 * but we support both formats for compatibility
 */
export interface ConversationDetailResponse extends Partial<ChatwootConversation> {
  data?: ChatwootConversation;
  payload?: ChatwootConversation;
}

/**
 * Response from toggle_status endpoint
 * POST /api/accounts/conversations/{conversation_id}/toggle_status
 */
export interface ConversationStatusToggleResponse {
  meta: Record<string, any>;
  payload: {
    success: boolean;
    current_status: string;
    current_status_id?: number | null;
    workflow_status?: ConversationWorkflowStatus | null;
    conversation_id: number;
    snoozed_until?: string | number | null;
  };
}
